import { ROUTES, ROUTE_PARAMS } from '../../../app-core';
import { interpolatePathParams, joinRouteMatchUrl, joinRouteMatchPath, getPathMatch } from '../../../path';
import {
  getWorkspaceDetailRoutePath,
  getWorkspaceDetailSettingsRoutePath,
  getWorkspaceDetailSettingsUrl,
  getWorkspaceDetailUrl
} from '../../../workspaces';

// ***************
// * Route paths *
// ***************

export const getConsolidationsRoutePath = (): string => {
  return joinRouteMatchUrl('', getWorkspaceDetailRoutePath(), ROUTES.CONSOLIDATIONS_ROUTE);
};

export const getConsolidationsSettingsRoutePath = (): string => {
  return joinRouteMatchUrl('', getWorkspaceDetailSettingsRoutePath(), ROUTES.CONSOLIDATIONS_ROUTE);
};

export const getConsolidationDetailRoutePath = (): string => {
  return joinRouteMatchUrl('', getConsolidationsRoutePath(), ROUTES.CONSOLIDATION_BY_ID);
};

export const getConsolidationProductRoutePath = (): string => {
  return joinRouteMatchUrl('', getConsolidationDetailRoutePath(), ROUTES.PRODUCT_SLUG_ROUTE);
};

export const getConsolidatedForecastingRoutePath = (): string => {
  return joinRouteMatchUrl('', getConsolidationProductRoutePath(), ROUTES.FORECASTS);
};

export const getConsolidatedForecastImportRoutePath = (): string => {
  return joinRouteMatchUrl('', getConsolidatedForecastingRoutePath(), ROUTES.FORECAST_BY_ID, ROUTES.IMPORT);
};

export const getConsolidatedForecastRoutePath = (): string => {
  return joinRouteMatchUrl('', getConsolidatedForecastingRoutePath(), ROUTES.FORECAST_BY_ID);
};

export const getConsolidatedForecastPredictionsRoutePath = (): string => {
  return joinRouteMatchUrl('', getConsolidatedForecastRoutePath(), ROUTES.PREDICTIONS);
};

export const getConsolidatedForecastSalesRoutePath = (): string => {
  return joinRouteMatchUrl('', getConsolidatedForecastPredictionsRoutePath(), ROUTES.SALES);
};

export const getCreateConsolidationRoutePath = (): string => {
  return joinRouteMatchPath(
    '',
    getConsolidationsSettingsRoutePath(),
    ROUTES.CREATE_CONSOLIDATION,
    ROUTES.CONSOLIDATION_TAB
  );
};

export const getEditConsolidationRoutePath = (): string => {
  return joinRouteMatchPath(
    '',
    getConsolidationsSettingsRoutePath(),
    ROUTES.CONSOLIDATION_BY_ID,
    ROUTES.CONSOLIDATION_TAB
  );
};

// *************
// * Page URLs *
// *************

export const getConsolidationsUrl = (workspaceId: string): string => {
  return joinRouteMatchUrl('', getWorkspaceDetailUrl(workspaceId), ROUTES.CONSOLIDATIONS_ROUTE);
};

export const getConsolidationsSettingsUrl = (workspaceId: string): string => {
  return joinRouteMatchUrl(getWorkspaceDetailSettingsUrl(workspaceId), ROUTES.CONSOLIDATIONS_ROUTE);
};

export const getConsolidationDetailUrl = (workspaceId: string, consolidationId: string): string => {
  const path = joinRouteMatchUrl(getConsolidationsUrl(workspaceId), ROUTES.CONSOLIDATION_BY_ID);
  return interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId
  });
};

export const getConsolidationProductUrl = (workspaceId: string, consolidationId: string, productId: string): string => {
  const path = joinRouteMatchUrl(getConsolidationDetailUrl(workspaceId, consolidationId), ROUTES.PRODUCT_SLUG_ROUTE);
  return interpolatePathParams(path, {
    [ROUTE_PARAMS.PRODUCT_SLUG]: productId
  });
};

export const getConsolidatedForecastingUrl = (
  workspaceId: string,
  consolidationId: string,
  productId: string
): string => {
  return joinRouteMatchUrl('', getConsolidationProductUrl(workspaceId, consolidationId, productId), ROUTES.FORECASTS);
};

export const getConsolidatedForecastImportUrl = (
  workspaceId: string,
  consolidationId: string,
  productId: string,
  forecastId: string
): string => {
  const path = joinRouteMatchUrl(
    '',
    getConsolidatedForecastingUrl(workspaceId, consolidationId, productId),
    ROUTES.FORECAST_BY_ID,
    ROUTES.IMPORT
  );
  return interpolatePathParams(path, {
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });
};

export const getConsolidatedForecastUrl = (
  workspaceId: string,
  consolidationId: string,
  productId: string,
  forecastId: string
): string => {
  const path = joinRouteMatchUrl(
    '',
    getConsolidatedForecastingUrl(workspaceId, consolidationId, productId),
    ROUTES.FORECAST_BY_ID
  );
  return interpolatePathParams(path, {
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });
};

export const getConsolidatedForecastPredictionsUrl = (
  workspaceId: string,
  consolidationId: string,
  productId: string,
  forecastId: string
): string => {
  return joinRouteMatchUrl(
    '',
    getConsolidatedForecastUrl(workspaceId, consolidationId, productId, forecastId),
    ROUTES.PREDICTIONS
  );
};

export const getConsolidatedForecastSalesUrl = (
  workspaceId: string,
  consolidationId: string,
  productId: string,
  forecastId: string
): string => {
  return joinRouteMatchUrl(
    '',
    getConsolidatedForecastPredictionsUrl(workspaceId, consolidationId, productId, forecastId),
    ROUTES.SALES
  );
};

export const getCreateConsolidationUrl = (workspaceId: string, tab = ''): string => {
  const path = joinRouteMatchUrl(
    '',
    joinRouteMatchUrl('', getWorkspaceDetailSettingsUrl(workspaceId), ROUTES.CONSOLIDATIONS_ROUTE),
    ROUTES.CREATE_CONSOLIDATION,
    ROUTES.CONSOLIDATION_TAB
  );
  return interpolatePathParams(path, {
    [ROUTE_PARAMS.TAB_ID]: tab
  })
    .replace('/?', '')
    .replace('?', '');
};

export const getEditConsolidationUrl = (workspaceId: string, consolidationId: string, tab = ''): string => {
  const path = joinRouteMatchUrl(
    '',
    getConsolidationsSettingsUrl(workspaceId),
    ROUTES.CONSOLIDATION_BY_ID,
    ROUTES.CONSOLIDATION_TAB
  );
  return interpolatePathParams(path, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.TAB_ID]: tab
  });
};

// ******************
// * Route matchers *
// ******************

export const matchConsolidationsRoutePath = (pathname: string, isExact?: boolean) => {
  const routePath = getConsolidationsRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};

export const matchConsolidationsSettingsRoutePath = (pathname: string, isExact?: boolean) => {
  const routePath = getConsolidationsSettingsRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};

export const matchConsolidationDetailRoutePath = (pathname: string, isExact?: boolean) => {
  const routePath = getConsolidationDetailRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};

export const matchConsolidationProductRoutePath = (pathname: string, isExact?: boolean) => {
  const routePath = getConsolidationProductRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};

export const matchConsolidatedForecastingRoutePath = (pathname: string, isExact?: boolean) => {
  let routePath = getConsolidatedForecastingRoutePath();
  routePath = isExact ? routePath : `${routePath}/*`;
  return getPathMatch(pathname, routePath, isExact);
};

export const matchConsolidatedForecastRoutePath = (pathname: string, isExact?: boolean) => {
  const routePath = getConsolidatedForecastRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};

export const matchConsolidatedForecastPredictionsRoutePath = (pathname: string, isExact?: boolean) => {
  const routePath = getConsolidatedForecastPredictionsRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};

export const matchConsolidatedForecastImportRoutePath = (pathname: string, isExact?: boolean) => {
  const routePath = getConsolidatedForecastImportRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};

export const matchConsolidatedForecastSalesRoutePath = (pathname: string, isExact?: boolean) => {
  const routePath = getConsolidatedForecastSalesRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};

export const matchCreateConsolidationRoutePath = (pathname: string, isExact?: boolean) => {
  const routePath = getCreateConsolidationRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};

export const matchEditConsolidationRoutePath = (pathname: string, isExact?: boolean) => {
  const routePath = getEditConsolidationRoutePath();
  return getPathMatch(pathname, routePath, isExact);
};
